import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr, enUS, ar } from 'date-fns/locale';
import { Inspection } from '../../types/inspection';
import { User } from '../../types/auth';
import { FileText, X, Plus, Minus, Download, Calculator } from 'lucide-react';
import logo from '../../img/logo.png';
import { quoteService } from '../../services/quotesService';
import { useLanguage } from '../../hooks/useLanguage';
import { toast } from 'react-hot-toast';

interface QuoteItem {
    reference: string;
    description: string;
    quantity: number;
    unitPriceHT: number;
    tvaRate: number;
    discountPercent: number;
}

interface QuoteModalProps {
    inspection: Inspection;
    garageInfo: User;
    onClose: () => void;
    onQuoteCreated?: () => void;
}

interface BusinessHours { open: string; close: string; closed: boolean; }
interface Address { street: string; zipCode: string; city: string; country: string; latitude: number; longitude: number; }
interface GarageUser {
    _id: string; firstName: string; lastName: string; email: string; phoneNumber: string;
    garageName: string; siretNumber: string; address: Address; garagePhone: string;
    website: string; specialities: string[]; vehicleTypes: string[];
    businessHours: { MONDAY: BusinessHours; };
}
interface ItemTotals { subtotalHT: number; tvaAmount: number; subtotalTTC: number; discountAmount: number; finalPrice: number; }

const garageId = JSON.parse(localStorage.getItem('user') || '{}')._id || '';

export const QuoteModal: React.FC<QuoteModalProps> = ({ inspection, garageInfo, onClose, onQuoteCreated }) => {
    const { t, currentLanguage, isRTL } = useLanguage();

    const [items, setItems] = useState<QuoteItem[]>([{ reference: '', description: '', quantity: 1, unitPriceHT: 0, tvaRate: 20, discountPercent: 0 }]);
    const [notes, setNotes] = useState('');
    const [savedQuoteId, setSavedQuoteId] = useState<any>(null);
    const [user] = useState<GarageUser>(JSON.parse(localStorage.getItem('user') || '{}'));
    const [loading, setLoading] = useState(false);
    const defaultTVA = 20;

    const getDateLocale = () => {
        switch (currentLanguage) { case 'fr': return fr; case 'ar': return ar; default: return enUS; }
    };

    const formatDate = (date: Date): string => {
        if (currentLanguage === 'ar') return format(date, 'dd/MM/yyyy', { locale: getDateLocale() });
        const d = date.getDate().toString().padStart(2, '0');
        const m = (date.getMonth() + 1).toString().padStart(2, '0');
        return `${d}/${m}/${date.getFullYear()}`;
    };

    const formatDateTime = (date: Date): string => date.toISOString().replace(/[^\d]/g, '').slice(0, 14);

    const calculateItemTotals = (item: QuoteItem): ItemTotals => {
        const subtotalHT = item.quantity * item.unitPriceHT;
        const tvaAmount = subtotalHT * (item.tvaRate / 100);
        const subtotalTTC = subtotalHT + tvaAmount;
        const discountAmount = subtotalTTC * (item.discountPercent / 100);
        const finalPrice = subtotalTTC - discountAmount;
        return { subtotalHT, tvaAmount, subtotalTTC, discountAmount, finalPrice };
    };

    const calculateTotals = () => items.reduce((acc, item) => {
        const t = calculateItemTotals(item);
        return { totalHT: acc.totalHT + item.quantity * item.unitPriceHT, totalTVA: acc.totalTVA + t.tvaAmount, totalDiscount: acc.totalDiscount + t.discountAmount, totalTTC: acc.totalTTC + t.finalPrice };
    }, { totalHT: 0, totalTVA: 0, totalDiscount: 0, totalTTC: 0 });

    const addItem = () => setItems([...items, { reference: '', description: '', quantity: 1, unitPriceHT: 0, tvaRate: defaultTVA, discountPercent: 0 }]);

    const updateItem = (index: number, field: keyof QuoteItem, value: number | string) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], [field]: typeof value === 'string' ? value : Number(value) };
        setItems(newItems);
    };

    const removeItem = (index: number) => setItems(items.filter((_, i) => i !== index));

    const handleSubmit = async () => {
        const hasValidItems = items.some(item => item.description.trim() !== '');
        if (!hasValidItems) { toast.error(currentLanguage === 'fr' ? '❌ Veuillez ajouter au moins un article avec une description' : '❌ Please add at least one item with a description'); return; }
        const hasInvalidPrices = items.some(item => item.unitPriceHT < 0);
        if (hasInvalidPrices) { toast.error(currentLanguage === 'fr' ? '❌ Veuillez entrer des prix valides' : '❌ Please enter valid prices'); return; }

        try {
            setLoading(true);
            const gId = JSON.parse(localStorage.getItem('user') || '{}')._id || '';
            const quoteData = {
                quoteNumber: `D-${formatDateTime(new Date())}`,
                garageId: gId,
                inspectionId: inspection._id,
                items: items.map(item => ({ ...item, totalHT: item.quantity * item.unitPriceHT, totalTTC: calculateItemTotals(item).finalPrice })),
                totalHT: calculateTotals().totalHT,
                totalTVA: calculateTotals().totalTVA,
                totalDiscount: calculateTotals().totalDiscount,
                totalTTC: calculateTotals().totalTTC,
                notes,
                validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
            };
            const result = await quoteService.createQuote(quoteData);
            setSavedQuoteId(result._id);
            await quoteService.updateQuoteStatus(result._id, "sent");
            toast.success(currentLanguage === 'fr' ? '✅ Devis envoyé avec succès' : '✅ Quote sent successfully');
            if (onQuoteCreated) onQuoteCreated();
            onClose();
        } catch (error) {
            console.error('Error creating quote:', error);
            toast.error(currentLanguage === 'fr' ? '❌ Erreur lors de l\'envoi du devis' : '❌ Error sending quote');
        } finally {
            setLoading(false);
        }
    };

    const handleGeneratePDF = () => {
        if (savedQuoteId) quoteService.downloadQuotePDF(savedQuoteId);
        else alert(t('quoteModal.alerts.saveFirstMessage'));
    };

    const getLoadingText = () => {
        switch (currentLanguage) { case 'ar': return 'جاري الإرسال...'; case 'fr': return 'Envoi en cours...'; default: return 'Sending...'; }
    };

    const quoteNumber = `D-${formatDateTime(new Date())}`;
    const currentDate = formatDate(new Date());
    const totals = calculateTotals();

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
            <div className={`bg-white w-full h-full overflow-y-auto sm:rounded-lg sm:h-auto sm:max-h-[90vh] sm:max-w-7xl sm:m-4 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>

                {/* Header */}
                <div className={`flex justify-between items-start p-4 sm:p-6 border-b sticky top-0 bg-white z-10 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div>
                        <h2 className="text-base sm:text-2xl font-bold">{t('quoteModal.title')} {t('quoteModal.number')} {quoteNumber}</h2>
                        <p className="text-xs sm:text-sm text-gray-500">{t('quoteModal.date')}: {currentDate}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full flex-shrink-0" disabled={loading}>
                        <X className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                </div>

                <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                    {/* Garage & Client info — stack on mobile */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
                        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg text-sm">
                            <h3 className="font-bold mb-2">{user.garageName}</h3>
                            <p>{user.address.street}</p>
                            <p>{user.address.zipCode} {user.address.city}</p>
                            <p>SIRET: {user?.siretNumber}</p>
                            <p>{t('quoteModal.garageInfo.phone')}: {user?.phoneNumber}</p>
                        </div>
                        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg text-sm">
                            <h3 className="font-bold mb-2">{t('quoteModal.clientInfo.title')}</h3>
                            <p>{inspection.clientName}</p>
                            <p>{inspection.location.address}</p>
                            <p>{t('quoteModal.clientInfo.vehicle')}: {inspection.vehicleBrand} {inspection.vehicleModel}</p>
                            <p>{t('quoteModal.clientInfo.year')}: {inspection.vehicleYear}</p>
                        </div>
                    </div>

                    {/* Items table — horizontal scroll on mobile */}
                    <div className="overflow-x-auto -mx-4 sm:mx-0">
                        <div className="inline-block min-w-full px-4 sm:px-0">
                            <table className={`w-full text-sm ${isRTL ? 'rtl' : ''}`}>
                                <thead className="bg-gray-50">
                                <tr>
                                    {[
                                        t('quoteModal.table.headers.reference'),
                                        t('quoteModal.table.headers.description'),
                                        t('quoteModal.table.headers.quantity'),
                                        t('quoteModal.table.headers.unitPriceHT'),
                                        t('quoteModal.table.headers.vatRate'),
                                        t('quoteModal.table.headers.vatAmount'),
                                        t('quoteModal.table.headers.discountPercent'),
                                        t('quoteModal.table.headers.discountAmount'),
                                        t('quoteModal.table.headers.totalTTC'),
                                        ''
                                    ].map((label, i) => (
                                        <th key={i} className={`px-2 sm:px-4 py-2 text-xs ${i < 2 ? (isRTL ? 'text-right' : 'text-left') : 'text-right'}`}>{label}</th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                {items.map((item, index) => {
                                    const it = calculateItemTotals(item);
                                    return (
                                        <tr key={index} className="border-b">
                                            <td className="px-2 sm:px-4 py-2">
                                                <input type="text" value={item.reference} onChange={(e) => updateItem(index, 'reference', e.target.value)} className={`w-full min-w-[80px] border rounded px-2 py-1 text-xs sm:text-sm ${isRTL ? 'text-right' : ''}`} placeholder={t('quoteModal.table.placeholders.reference')} disabled={loading} />
                                            </td>
                                            <td className="px-2 sm:px-4 py-2">
                                                <input type="text" value={item.description} onChange={(e) => updateItem(index, 'description', e.target.value)} className={`w-full min-w-[120px] border rounded px-2 py-1 text-xs sm:text-sm ${isRTL ? 'text-right' : ''}`} placeholder={t('quoteModal.table.placeholders.description')} disabled={loading} />
                                            </td>
                                            <td className="px-2 sm:px-4 py-2"><input type="number" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', e.target.value)} className="w-14 sm:w-20 border rounded px-1 py-1 text-right text-xs sm:text-sm" min="1" disabled={loading} /></td>
                                            <td className="px-2 sm:px-4 py-2"><input type="number" value={item.unitPriceHT} onChange={(e) => updateItem(index, 'unitPriceHT', e.target.value)} className="w-16 sm:w-24 border rounded px-1 py-1 text-right text-xs sm:text-sm" step="0.01" min="0" disabled={loading} /></td>
                                            <td className="px-2 sm:px-4 py-2"><input type="number" value={item.tvaRate} onChange={(e) => updateItem(index, 'tvaRate', e.target.value)} className="w-14 sm:w-20 border rounded px-1 py-1 text-right text-xs sm:text-sm" min="0" max="100" disabled={loading} /></td>
                                            <td className="px-2 sm:px-4 py-2 text-right whitespace-nowrap text-xs sm:text-sm">{it.tvaAmount.toFixed(2)} {t('quoteModal.currency')}</td>
                                            <td className="px-2 sm:px-4 py-2"><input type="number" value={item.discountPercent} onChange={(e) => updateItem(index, 'discountPercent', e.target.value)} className="w-14 sm:w-20 border rounded px-1 py-1 text-right text-xs sm:text-sm" min="0" max="100" disabled={loading} /></td>
                                            <td className="px-2 sm:px-4 py-2 text-right whitespace-nowrap text-xs sm:text-sm">{it.discountAmount.toFixed(2)} {t('quoteModal.currency')}</td>
                                            <td className="px-2 sm:px-4 py-2 text-right font-bold whitespace-nowrap text-xs sm:text-sm">{it.finalPrice.toFixed(2)} {t('quoteModal.currency')}</td>
                                            <td className="px-2 sm:px-4 py-2">
                                                <button onClick={() => removeItem(index)} className="text-red-600 hover:bg-red-50 p-1 rounded disabled:opacity-50" disabled={items.length === 1 || loading}>
                                                    <Minus className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Add item */}
                    <button onClick={addItem} className={`flex items-center text-blue-600 hover:text-blue-700 disabled:opacity-50 text-sm ${isRTL ? 'flex-row-reverse' : ''}`} disabled={loading}>
                        <Plus className={`w-5 h-5 ${isRTL ? 'ml-1' : 'mr-1'}`} />
                        {t('quoteModal.actions.addItem')}
                    </button>

                    {/* Totals */}
                    <div className={`bg-gray-50 p-3 sm:p-4 rounded-lg ${isRTL ? 'text-left' : 'text-right'}`}>
                        <div className={`grid grid-cols-2 gap-x-4 gap-y-1 max-w-xs ${isRTL ? 'mr-auto' : 'ml-auto'} text-sm`}>
                            <span className="text-gray-600">{t('quoteModal.totals.totalHT')}:</span>
                            <span>{totals.totalHT.toFixed(2)} {t('quoteModal.currency')}</span>
                            <span className="text-gray-600">{t('quoteModal.totals.totalVAT')}:</span>
                            <span>{totals.totalTVA.toFixed(2)} {t('quoteModal.currency')}</span>
                            <span className="text-gray-600">{t('quoteModal.totals.totalDiscount')}:</span>
                            <span>-{totals.totalDiscount.toFixed(2)} {t('quoteModal.currency')}</span>
                            <span className="font-bold">{t('quoteModal.totals.totalTTC')}:</span>
                            <span className="font-bold text-blue-600">{totals.totalTTC.toFixed(2)} {t('quoteModal.currency')}</span>
                        </div>
                    </div>

                    {/* Notes */}
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('quoteModal.notes.placeholder')} className={`w-full p-3 sm:p-4 border rounded-lg h-24 sm:h-32 text-sm ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'} disabled={loading} />

                    {/* Actions */}
                    <div className={`flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-4 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                        <button onClick={handleGeneratePDF} className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 disabled:opacity-50 text-sm" disabled={loading}>
                            <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                            {t('quoteModal.actions.download')}
                        </button>
                        <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 text-sm order-last sm:order-none" disabled={loading}>
                            {t('quoteModal.actions.cancel')}
                        </button>
                        <button onClick={handleSubmit} className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm sm:text-base" disabled={loading}>
                            {loading ? (
                                <><div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-t-2 border-b-2 border-white"></div><span>{getLoadingText()}</span></>
                            ) : (
                                <><FileText className="w-4 h-4 sm:w-5 sm:h-5" />{t('quoteModal.actions.sendQuote')}</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};